import { IS_MOBILE } from '@constants/global';
import { ChevronIcon, PlusIcon } from '@icons/IconLibrary';
import ProjectsState from '@states/Projects';
import { UIButton, UIDiv, UIText } from '@ui';
export default class ProjectListSkeleton extends UIDiv {
  constructor({
    rowCount = 8,
    // Those parameters are needed to decide whether to show breadcrumb and add task row
    folderId,
    canCreate = true,
    sharedProjectId = null,
  } = {}) {
    super();
    this.rowCount = rowCount;
    this.folderId = folderId;
    this.canCreate = canCreate;
    this.sharedProjectId = sharedProjectId;
    this.setId('project-list-skeleton');
    this.setClass(
      'flex flex-col w-full h-full border border-gray-200 rounded-lg shadow-sm overflow-hidden',
    );

    this.#render();
  }

  #render() {
    // Header section (sticky top)
    const headerSection = new UIDiv();
    headerSection.setClass('sticky top-0 bg-white z-30 min-w-fit');

    if (this.folderId) {
      // Breadcrumb row - actual data, not skeleton
      const breadcrumbRow = this.#createBreadcrumbRow();
      headerSection.add(breadcrumbRow);
    }

    // Header row skeleton
    const headerRow = this.#createHeaderSkeleton();
    headerSection.add(headerRow);

    // Add task row - actual UI, not skeleton (if user can create)
    if (this.folderId && this.canCreate) {
      const addTaskRow = this.#createAddTaskRow();
      headerSection.add(addTaskRow);
    }

    this.add(headerSection);
    // Data rows skeleton
    const rowsContainer = new UIDiv();
    rowsContainer.setClass('flex flex-col flex-1 overflow-hidden');

    for (let i = 0; i < this.rowCount; i++) {
      const row = this.#createRowSkeleton(i);
      rowsContainer.add(row);
    }

    this.add(rowsContainer);
  }

  #createBreadcrumbRow() {
    const breadcrumbContainer = new UIDiv();
    breadcrumbContainer.addClass(
      'w-full p-3 bg-gray-50 border-b border-gray-200 flex items-center gap-1',
    );

    // Back button
    const backButton = new UIButton(i18next.t('pages.projects.projects'));
    backButton.addClass('!filter-none text-sm text-gray-600 font-medium');

    const chevronIcon = new ChevronIcon({
      size: 12,
      color: 'none',
    });

    const currentFolder = ProjectsState.projects.folders.find(
      f => f.id === this.folderId,
    );
    const folderName = currentFolder
      ? currentFolder.folderName || currentFolder.name
      : null;

    if (!this.sharedProjectId) {
      breadcrumbContainer.add(backButton);
      breadcrumbContainer.add(chevronIcon);
    } else {
      // If in shared project view, don't show back button and just show a label with 'Shared Project'
      const sharedLabel = new UIText(i18next.t('common.shared_projects'));
      sharedLabel.addClass('text-sm font-semibold text-gray-600');
      breadcrumbContainer.add(sharedLabel);
      breadcrumbContainer.add(chevronIcon);
    }

    // Show folder name or skeleton placeholder if not available yet
    if (folderName) {
      const folderLabel = new UIText(folderName);
      folderLabel.addClass('text-sm font-semibold');
      breadcrumbContainer.add(folderLabel);
    } else {
      // Show skeleton placeholder for folder name
      const folderNameSkeleton = new UIDiv();
      folderNameSkeleton.addClass('w-24 h-4 bg-gray-200 rounded animate-pulse');
      breadcrumbContainer.add(folderNameSkeleton);
    }

    return breadcrumbContainer;
  }

  #createHeaderSkeleton() {
    const header = new UIDiv();
    header.setClass(
      'flex items-center gap-3 px-4 py-3 bg-gray-50 border-b border-gray-200',
    );

    // Column headers skeleton
    const columnWidths = ['w-1/4', 'w-1/6', 'w-1/6', 'w-1/6', 'w-1/6'];
    columnWidths.forEach(width => {
      const column = new UIDiv();
      column.setClass(`${width} h-4 bg-gray-200 rounded animate-pulse`);
      header.add(column);
    });

    return header;
  }

  #createAddTaskRow() {
    const addTaskContainer = new UIDiv();
    addTaskContainer.setClass(
      'flex h-12 w-full items-center gap-2 px-2 border-b border-gray-200 cursor-default',
    );

    const icon = new PlusIcon({
      size: 16,
      color: 'none',
      strokeColor: null,
    });

    const textElement = new UIText(i18next.t('pages.projects.addTask'));
    textElement.setClass('text-gray-400');

    addTaskContainer.add(icon, textElement);

    return addTaskContainer;
  }

  #createRowSkeleton(index) {
    const row = new UIDiv();
    row.setClass(
      `flex items-center gap-3 px-4 py-3 border-b border-gray-100 ${
        index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'
      }`,
    );

    // Fake checkbox placeholder
    if (IS_MOBILE()) {
      const checkbox = new UIDiv();
      checkbox.setClass(
        'w-6 h-8 flex-shrink-0 bg-gray-200 rounded animate-pulse',
      );
      row.add(checkbox);
    }

    // Name column (wider)
    const nameWidth = ['w-32', 'w-40', 'w-36', 'w-44'][index % 4];
    const name = new UIDiv();
    name.setClass(`${nameWidth} h-8 bg-gray-200 rounded animate-pulse`);
    row.add(name);

    const fillingRow = new UIDiv();
    fillingRow.setClass('w-full animate-pulse bg-gray-200 rounded h-8');
    row.add(fillingRow);

    // Actions placeholder
    const actions = new UIDiv();
    actions.setClass('w-8 h-8 bg-gray-200 rounded animate-pulse flex-shrink-0');
    row.add(actions);

    return row;
  }
}
